import {Link} from "react-router-dom";

/**
 * Documentation-first home page. Answers, in order: what it is, how it works,
 * the tech, and where to go next. No marketing filler.
 */
export function Home() {
  return (
    <div className="space-y-12">
      {/* Hero */}
      <section className="relative panel p-8 md:p-12 overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-px bg-molten-line animate-flow" />
        <p className="label mb-3">Ritual Chain · testnet 1979</p>
        <h1 className="font-display text-4xl md:text-5xl font-bold text-forge-ash leading-tight max-w-3xl">
          Raw public web data, smelted into structured, payable JSON.
        </h1>
        <p className="mt-5 max-w-2xl text-forge-slag text-lg leading-relaxed">
          Smelt is a sovereign onchain agent. It fetches free, messy public filings
          from SEC EDGAR, parses them into a rigid five-metric schema inside a TEE,
          stores the result on-chain, and serves it through a paid query. The
          contract is the backend. No off-chain server, no centralized database.
        </p>
        <div className="mt-7 flex flex-wrap gap-3">
          <Link to="/console" className="btn-primary">Open the console</Link>
          <Link to="/filings" className="btn-ghost">Browse filings</Link>
        </div>
      </section>

      {/* How it works */}
      <section>
        <h2 className="font-display text-2xl font-semibold text-forge-ash mb-5">How it works</h2>
        <div className="grid md:grid-cols-4 gap-4">
          <Step n="01" title="Fetch" body="A scheduled transaction calls the HTTP precompile (0x0801) to pull a raw SEC EDGAR filing into contract state." />
          <Step n="02" title="Smelt" body="A second scheduled transaction passes the raw text to the LLM precompile (0x0802) with a strict JSON schema. Only one async call fits per block." />
          <Step n="03" title="Store" body="The structured five-metric result (Revenue, Net Income, Total Debt, EPS, Free Cash Flow) is written to on-chain storage, keyed by ticker and period." />
          <Step n="04" title="Serve" body="Anyone pays the query fee and reads the filing through an on-chain function. Fees accrue to a pull-payment balance the recipient claims." />
        </div>
        <p className="mt-4 text-xs text-forge-ash mono">
          The fetch and smelt steps run as two scheduled callbacks chained by contract
          state, offset by ~500 blocks so the HTTP call settles first.
        </p>
      </section>

      {/* Why Ritual */}
      <section className="grid md:grid-cols-2 gap-4">
        <div className="panel p-6">
          <h3 className="font-display text-lg text-molten-200 mb-2">Why this needs Ritual</h3>
          <p className="text-sm text-forge-slag leading-relaxed">
            On a normal L1, serving an API and running an LLM means an off-chain
            server, a database, and an oracle to sync state back. Ritual enshrines
            HTTP, LLM inference, and scheduling as native precompiles verified by TEE
            attestation, so the smart contract can fetch, reason, and settle in one
            verifiable flow. Remove the precompile stack and Smelt stops working.
          </p>
        </div>
        <div className="panel p-6">
          <h3 className="font-display text-lg text-molten-200 mb-2">The five metrics</h3>
          <ul className="text-sm text-forge-slag space-y-1.5 font-mono">
            <li>· Revenue</li>
            <li>· Net Income</li>
            <li>· Total Debt</li>
            <li>· EPS</li>
            <li>· Free Cash Flow</li>
          </ul>
          <p className="mt-3 text-xs text-forge-ash">
            Extracted per filing via the <span className="mono">zai-org/GLM-4.7-FP8</span>{" "}
            model with a low temperature and a fixed seed. Structured output is
            best-effort; failed parses retry on the next scheduled run.
          </p>
        </div>
      </section>

      {/* Tech */}
      <section>
        <h2 className="font-display text-2xl font-semibold text-forge-ash mb-4">Stack</h2>
        <div className="flex flex-wrap gap-2">
          {["Solidity 0.8.24", "Foundry", "OpenZeppelin", "HTTP precompile 0x0801", "LLM precompile 0x0802", "Scheduler", "RitualWallet", "React", "Vite", "viem", "wagmi", "RainbowKit"].map(
            (t) => (
              <span key={t} className="px-3 py-1 rounded border border-forge-border bg-forge-surface text-xs font-mono text-forge-slag">
                {t}
              </span>
            )
          )}
        </div>
      </section>
    </div>
  );
}

function Step({n, title, body}: {n: string; title: string; body: string}) {
  return (
    <div className="panel p-5 relative">
      <span className="absolute top-4 right-4 font-mono text-xs text-molten-600">{n}</span>
      <h3 className="font-display text-lg text-forge-ash mb-2">{title}</h3>
      <p className="text-sm text-forge-slag leading-relaxed">{body}</p>
    </div>
  );
}
